import { useState, useRef } from "react";
import "./editorialForms.css";

export default function PodcastForm({ data, onChange, onAudioUpload }) {
  const [guests, setGuests] = useState(data?.guests?.join(", ") || "");
  const fileInputRef = useRef(null);
  const coverImageInputRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      onChange({ ...data, audioFile: file, audioFileName: file.name });
    }
  };

  const handleCoverImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        onChange({
          ...data,
          coverImage: file,
          coverImageName: file.name,
          coverImagePreview: reader.result,
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleGuestsChange = (e) => {
    const value = e.target.value;
    setGuests(value);
    const guestArray = value
      .split(",")
      .map((guest) => guest.trim())
      .filter((guest) => guest.length > 0);
    onChange({ ...data, guests: guestArray });
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <div className="editorial_form podcast_form">
      <div className="editorial_form_section">
        <label>
          Arquivo de Áudio <span className="required">*</span>
        </label>
        <div className="file_upload_area" onClick={() => fileInputRef.current?.click()}>
          <input
            ref={fileInputRef}
            type="file"
            accept="audio/*,.mp3,.wav,.m4a,.ogg,.aac"
            onChange={handleFileChange}
            style={{ display: "none" }}
          />
          {data?.audioFileName ? (
            <div className="file_selected">
              <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z" />
              </svg>
              <span>{data.audioFileName}</span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange({ ...data, audioFile: null, audioFileName: null });
                }}
                className="file_remove_btn"
              >
                ✕
              </button>
            </div>
          ) : (
            <div className="file_placeholder">
              <svg viewBox="0 0 24 24" fill="currentColor" width="32" height="32">
                <path d="M9 16h6v-6h4l-7-7-7 7h4zm-4 2h14v2H5z" />
              </svg>
              <p>Clique para fazer upload do episódio</p>
              <span>MP3, WAV, M4A, OGG, AAC</span>
            </div>
          )}
        </div>
      </div>

      <div className="editorial_form_section">
        <label>Imagem de Capa (opcional)</label>
        <div className="file_upload_area" onClick={() => coverImageInputRef.current?.click()}>
          <input
            ref={coverImageInputRef}
            type="file"
            accept="image/*,.jpg,.jpeg,.png,.webp"
            onChange={handleCoverImageChange}
            style={{ display: "none" }}
          />
          {data?.coverImagePreview ? (
            <div className="file_selected sticker_preview">
              <img src={data.coverImagePreview} alt="Capa" style={{ maxWidth: "100px", maxHeight: "100px", borderRadius: "8px" }} />
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange({ ...data, coverImage: null, coverImageName: null, coverImagePreview: null });
                }}
                className="file_remove_btn"
              >
                ✕
              </button>
            </div>
          ) : (
            <div className="file_placeholder">
              <svg viewBox="0 0 24 24" fill="currentColor" width="32" height="32">
                <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
              </svg>
              <p>Clique para fazer upload da capa</p>
              <span>JPG, PNG, WEBP</span>
            </div>
          )}
        </div>
      </div>

      <div className="editorial_form_section">
        <label>
          Título do Episódio <span className="required">*</span>
        </label>
        <input
          type="text"
          placeholder="Nome do episódio"
          value={data?.title || ""}
          onChange={(e) => onChange({ ...data, title: e.target.value })}
        />
      </div>

      <div className="editorial_form_section">
        <label>Nome do Podcast</label>
        <input
          type="text"
          placeholder="Nome do programa"
          value={data?.showName || ""}
          onChange={(e) => onChange({ ...data, showName: e.target.value })}
        />
      </div>

      <div className="editorial_form_section">
        <label>Descrição</label>
        <textarea
          placeholder="Sobre o que é este episódio..."
          value={data?.description || ""}
          onChange={(e) => onChange({ ...data, description: e.target.value })}
          rows={3}
          maxLength={2000}
        />
      </div>

      <div className="editorial_form_row">
        <div className="editorial_form_section">
          <label>Temporada</label>
          <input
            type="number"
            placeholder="Nº"
            value={data?.seasonNumber || ""}
            onChange={(e) =>
              onChange({ ...data, seasonNumber: parseInt(e.target.value) || 0 })
            }
            min="1"
          />
        </div>
        <div className="editorial_form_section">
          <label>Episódio</label>
          <input
            type="number"
            placeholder="Nº"
            value={data?.episodeNumber || ""}
            onChange={(e) =>
              onChange({ ...data, episodeNumber: parseInt(e.target.value) || 0 })
            }
            min="1"
          />
        </div>
        <div className="editorial_form_section">
          <label>Duração</label>
          <input
            type="text"
            placeholder="mm:ss"
            value={formatDuration(data?.duration)}
            readOnly
            className="duration_display"
          />
        </div>
      </div>

      <div className="editorial_form_section">
        <label>Convidados</label>
        <input
          type="text"
          placeholder="Nome1, Nome2, Nome3"
          value={guests}
          onChange={handleGuestsChange}
        />
        <span className="editorial_form_hint">
          Separe os nomes por vírgula
        </span>
      </div>

      <div className="editorial_form_tip">
        <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
          <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z" />
        </svg>
        <span>
          Faça upload do áudio usando o botão de mídia. Formatos: MP3, WAV, M4A
        </span>
      </div>
    </div>
  );
}
